import {observer} from "mobx-react";
import {useResources} from "../../stores/ResourceProvider";
import {useLocation, useNavigate} from "react-router-dom";
import {Avatar, Box, Divider, IconButton, List, Typography, useTheme} from "@mui/material";
import React, {ReactElement, useEffect, useState} from "react";
import User from "../../model/User";
import Drawer, {closedDrawerWidth, openDrawerWidth} from "../../components/mui/Drawer";
import RoutesConfig, {RoutesConfigHelpers} from "../../RoutesConfig";
import {isNumber} from "../../model/ModelUtilities";
import Model from "../../model/Model";
import {UserType} from "../../API";
import Review, {ReviewStatus} from "../../model/Review";
import Page from "../../components/page/Page";
import MarginRow from "../../components/page/MarginRow";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NavListItem from "../../components/controls/NavListItem";
import GradingIcon from '@mui/icons-material/Grading';
import {isoToLocalDateString} from "../../stores/StoreUtilities";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import SavingsIcon from "@mui/icons-material/Savings";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ChecklistIcon from '@mui/icons-material/Checklist';
import TimelineReview from "./TimelineReview";
import IModelItem from "../../model/IModelItem";
import ReviewChecklist from "./ReviewChecklist";
import NetWorthReview from "./NetWorthReview";
import BudgetReview from "./BudgetReview";
import {advisorSettingsKey, IAdvisorSettings} from "../calc/CalcPage";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ClientMenu from "../../components/controls/ClientMenu";
import ReviewMenu from "./ReviewMenu";
import NewReviewDialog from "./NewReviewDialog";
import EditReviewDialog from "./EditReviewDialog";
import ReviewDialog, {ReviewStep} from "./ReviewDialog";

export interface IReviewPageProps {
  view?: string
  year?: string
}

const ReviewPage = observer((props: IReviewPageProps) => {
  const { userStore, accountStore, modelStore, notify } = useResources()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAdvisor, setIsAdvisor] = useState<boolean>(false)
  const [clientId, setClientId] = useState<string | undefined>()
  const [client, setClient] = useState<User | undefined>()
  const [isClient, setIsClient] = useState<boolean>(false)
  const [reviews, setReviews] = useState<Review[]>([])
  const [currentReview, setCurrentReview] = useState<Review | undefined>()
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState<string>("")
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState<boolean>(true)
  const [navDrawerWidth, setNavDrawerWidth] = useState<number>(openDrawerWidth)
  const [leftMargin, setLeftMargin] = useState<number>()
  const [width, setWidth] = useState(window.innerWidth)
  const [contentWidth, setContentWidth] = useState<number>(0)
  const [expandedListItems, setExpandedListItems] = useState<Set<string>>(new Set<string>([]))
  const [view, setView] = useState<string | undefined>()
  const [viewNames, setViewNames] = useState<string[]>([])
  const [year, setYear] = useState<number | undefined>()
  const [showNewReviewDialog, setShowNewReviewDialog] = useState<boolean>(false)
  const [editReview, setEditReview] = useState<Review | undefined>()
  const [showDialog, setShowDialog] = useState<boolean>(false)

  useEffect(() => {
    // console.log(`------------------------------`)
    // console.log(`isNavDrawerOpen = ${isNavDrawerOpen}`)
    // console.log(`width = ${width}`)
    if (isNavDrawerOpen) {
      setNavDrawerWidth(openDrawerWidth)
      // console.log(`setNavDrawerWidth(${openDrawerWidth})`)
      setLeftMargin(openDrawerWidth)
      // console.log(`setLeftMargin(${openDrawerWidth})`)
      setContentWidth(width - openDrawerWidth)
      // console.log(`setContentWidth(${width - openDrawerWidth})`)
    } else {
      setNavDrawerWidth(closedDrawerWidth)
      // console.log(`setNavDrawerWidth(${closedDrawerWidth})`)
      setLeftMargin(closedDrawerWidth + 16)
      // console.log(`setLeftMargin(${closedDrawerWidth + 16})`)
      setContentWidth(width - closedDrawerWidth - 16)
      // console.log(`setContentWidth(${width - closedDrawerWidth})`)
    }
  }, [width])

  useEffect(() => {
    const viewName = RoutesConfigHelpers.getPathSegment(location.pathname, 2)
    const year = RoutesConfigHelpers.getPathSegment(location.pathname, 3)

    const viewNames = ["checklist", "timeline", "networth", "budget"]
    setViewNames(viewNames)

    const isAdvisorOrAdmin = userStore.isAdvisorOrAdmin
    setIsAdvisor(isAdvisorOrAdmin)
    setIsClient(userStore.user !== undefined && userStore.user.userType === UserType.Client)

    if (isAdvisorOrAdmin) {
      if (viewName && viewNames.indexOf(viewName) >= 0) {
        setView(viewName)
        if (year) {
          const yearNum = parseInt(year)
          if (isNumber(yearNum)) {
            setYear(yearNum)
          }
        }
      } else {
        navigate(`${RoutesConfig.review.pathname}/checklist`)
      }
    }

    if (!userStore.isLoading && !modelStore.isLoading && userStore.user && !clientId) {
      if (isAdvisorOrAdmin) {
        const advisorSettings = getAdvisorSettings()
        if (advisorSettings && advisorSettings.clientId) {
          setClientId(advisorSettings.clientId)
          // setCurrentModelId(advisorSettings.clientModelId)
          // setCurrentPlanId(advisorSettings.clientPlanId)
        } else if (userStore.isLoading === false && userStore.user) {
          setClientId(userStore.user.id)
        }
        setShowDialog(false)
      } else {
        setClientId(userStore.user.id)
        setShowDialog(true)
      }
    }
  }, [navigate, userStore.isLoading, modelStore.isLoading, modelStore])

  useEffect(() => {
    if (clientId && !isLoading) {
      setIsLoading(true)
      loadClient(clientId, true)
    }
  }, [clientId])

  useEffect(() => {
    if (!isLoading && currentReview && currentReview.modelId) {
      loadModel(currentReview.modelId)
    }

  }, [currentModel, currentUpdatedAt])

  const getAdvisorSettings = () => {
    if (userStore.isAdvisorOrAdmin) {
      const data = window.localStorage.getItem(advisorSettingsKey)
      if (data) {
        const settings: IAdvisorSettings = JSON.parse(data)
        return settings
      }
    }
    return undefined
  }

  const setAdvisorSettings = (clientId: string) => {
    if (userStore.isAdvisorOrAdmin) {
      const data = {
        clientId: clientId
      }
      window.localStorage.setItem(advisorSettingsKey, JSON.stringify(data))
    }
  }

  const loadClient = async (clientId: string, forceLoad: boolean = false) => {
    let user : User | undefined
    if (userStore.user && clientId === userStore.user.id) {
      user = userStore.user
    } else {
      user = await userStore.getUser(clientId)
    }
    if (user) {
      setClient(user)
      setReviews(user.reviews)
      if (user.reviews.length > 0) {
        const review = user.reviews[0]
        if (userStore.isCustomer && (review.submittedOn || review.status === ReviewStatus.Declined)) {
          // Just in case to prevent a modal blackhole
          navigate(RoutesConfig.calc.pathname)
          return
        }
        await loadReview(review)
      }
      setIsLoading(false)
    }
  }

  const loadReview = async (review: Review) => {
    setCurrentReview(review)
    if (review.modelId) {
      const model = await modelStore.getCurrentModel(review.modelId)
      if (model) {
        setCurrentModel(model)
      }
    }
  }

  const loadModel = async (modelId: string) => {
    const model = await modelStore.getCurrentModel(modelId)
    if (model) {
      setCurrentModel(model)
      setCurrentUpdatedAt(model.updatedAt)
    }
  }

  const styles = {
    drawerLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
      paddingLeft: theme.spacing(6)
    },
    checkedIcon: {
      height: 24,
      width: 24,
      color: theme.palette.action.active
    },
    uncheckedIcon: {
      color: theme.palette.action.disabled,
      height: 24,
      width: 24
    },
    secondary: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"]
    },
    listLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
      paddingLeft: theme.spacing(6)
    },
    drawerButton: {
      color:"inherit",
      p:0,
      mr:1,
      width: 24,
      height: 24,
      backgroundColor: `#dfdfee`,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      }
    }
  }


  const setExpandedListItem = (id: string, expand: boolean) => {
    if (expand) {
      expandedListItems.add(id)
    } else {
      expandedListItems.delete(id)
    }
    setExpandedListItems(new Set(expandedListItems))
  }
  const onViewChange = (view: string) => {
    navigate(`${RoutesConfig.review.pathname}/${view}`)
  }

  const handleChangeClient = (user: User) => {
    if (!client || client.id !== user.id) {
      setClientId(user.id)
      setAdvisorSettings(user.id)
      setClient(undefined)
      setReviews([])
      setCurrentReview(undefined)
    }
  }

  const handleChangeReview = async (review: Review) => {
    setCurrentReview(review)
  }

  const handleSaveReview = async (review: Review) => {
    const index = reviews.findIndex((r: Review) => r.id === review.id)
    if (index >= 0) {
      reviews[index] = review
      reviews.sort((a: Review, b: Review) => b.reviewDate.localeCompare(a.reviewDate))
      setReviews([...reviews])
    }
    setCurrentReview(review)
  }

  const handleCreateReview = (review: Review) => {
    setExpandedListItem("reviews", false)
    if (client) {
      if (client.reviews.findIndex((r: Review) => r.id === review.id) < 0) {
        setReviews([review, ...client.reviews])
      } else {
        setReviews([...client!.reviews])
      }
    }
    loadReview(review)
  }

  const handleDeleteReview = async (review: Review) => {
    const index = reviews.findIndex((r: Review) => r.id === review.id)
    if (index >= 0) {
      reviews.splice(index, 1)
    }
    setReviews([...reviews])
    if (currentReview && currentReview.id === review.id) {
      if (reviews.length > 0) {
        loadReview(reviews[0])
      } else {
        setCurrentReview(undefined)
      }
    }
  }

  const handleChangeView = (newView: string) => {
    navigate(`${RoutesConfig.review.pathname}/${newView}`)
  }

  const toggleDrawer = () => {
    if (isNavDrawerOpen) {
      setIsNavDrawerOpen(false)
      // console.log(`setIsNavDrawerOpen(false)`)
      setNavDrawerWidth(closedDrawerWidth)
      setLeftMargin(closedDrawerWidth + 16)
      setContentWidth(width - closedDrawerWidth)
    } else {
      setIsNavDrawerOpen(true)
      // console.log(`setIsNavDrawerOpen(true)`)
      setNavDrawerWidth(openDrawerWidth)
      setLeftMargin(openDrawerWidth)
      setContentWidth(width - openDrawerWidth)
    }
  }

  const getPageTitle = (view?: string) => {
    let viewName = ""

    switch (view) {
      case "checklist":
        viewName = "Checklist"
        break
      case "timeline":
        viewName = "Timeline"
        break
      case "networth":
        viewName = "Assets & Liabilities"
        break
      case "budget":
        viewName = "Budget"
        break
    }
    return `Review ${viewName}`
  }

  const getReviewTitle = (review?: Review) => {
    if (review) {
      return `${isoToLocalDateString(review.reviewDate, "MMM d, yyyy")} Review`
    }
    return reviews.length > 0 ? "No review selected" : "No reviews"
  }

  const renderDrawer = () => {
    let avatar: ReactElement | undefined = undefined
    if (client && isAdvisor && client) {
      const bgcolor = (client.id === userStore.user?.id) ? theme.palette.primary.main : theme.palette.action.active
      avatar = <Avatar sx={{bgcolor: bgcolor, fontSize:13, width:25, height:25}}>
        {client.initials}
      </Avatar>
    }

    return (
      <Drawer variant="permanent" open={isNavDrawerOpen}
              PaperProps={{
                sx: {
                  backgroundColor: theme.palette.background.default,
                  marginTop: "62px",
                  borderStyle:"none"
                }
              }}
        // onMouseEnter={handleMouseEnterDrawer}
        // onMouseLeave={handleMouseLeaveDrawer}
      >
        <Divider />
        <Box sx={{display:"flex", justifyContent:"stretch", alignItems:"top",
          mt:3.5, pl:2, pb:0, mb:1}}>
          <Box sx={{display:"flex", flexGrow:0, width:closedDrawerWidth, p:0, m:0}}>
            <IconButton size="small" onClick={toggleDrawer} sx={styles.drawerButton}>
              {isNavDrawerOpen ?  <ChevronLeftIcon/> : <ChevronRightIcon/>}
            </IconButton>
          </Box>
          <Box sx={{display:"flex", flexGrow:1}}>
            <Typography variant="h5" color="primary">Review Navigator</Typography>
          </Box>
        </Box>
        <List dense>
          {client && isAdvisor &&
            <React.Fragment>
              <NavListItem id="client"
                           icon={avatar ?? <ManageAccountsIcon/>}
                           title={`Client: ${client.fullName}`}
                           primary={client.fullName}
                           expanded={expandedListItems.has("clients")}
                           onClick={() => {
                             setExpandedListItem("clients", !expandedListItems.has("clients"))
                           }}
                           onChange={(expanded: boolean) => setExpandedListItem("clients", expanded)}
                           drawerOpen={true}
              >
                <ClientMenu color="primary" client={client}
                            onChangeClient={(client: User) => {
                              setExpandedListItem("clients", false)
                              handleChangeClient(client)
                            }}
                />
              </NavListItem>
            </React.Fragment>
          }
          <NavListItem id="review"
                       icon={<GradingIcon/>}
                       title={getReviewTitle(currentReview)}
                       primary={<Typography variant="body2" sx={{whiteSpace:"normal", lineHeight:1.1, fontWeight:700}}>{getReviewTitle(currentReview)}</Typography>}
                       divider={true}
                       drawerOpen={true}
                       expanded={expandedListItems.has("reviews")}
                       indent={2.5}
                       onClick={() => {
                         setExpandedListItem("reviews", !expandedListItems.has("reviews"))
                       }}
                       onChange={(expanded: boolean) => setExpandedListItem("reviews", expanded)}
          >
            {client && isNavDrawerOpen && userStore.isAdvisorOrAdmin &&
              <Box display="flex" px={0} maxWidth={240} width="240px">
                <ReviewMenu value={currentReview} options={reviews}
                            onChange={handleChangeReview}
                            onNew={() => setShowNewReviewDialog(true)}
                            onEdit={(review: Review) => setEditReview(review)}
                            />
              </Box>
            }
          </NavListItem>
          <NavListItem id="checklist"
                       icon={<ChecklistIcon/>}
                       title="Checklist"
                       primary="Checklist"
                       selected={view === "checklist"}
                       onClick={onViewChange}
                       drawerOpen={true}
          />
          <NavListItem id="timeline"
                       icon={<AvTimerIcon/>}
                       title="Timeline"
                       primary="Timeline"
                       selected={view === "timeline"}
                       onClick={onViewChange}
                       drawerOpen={true}
          />
          <NavListItem id="networth"
                       icon={<SavingsIcon/>}
                       title="Assets & Liabilities"
                       primary="Assets & Liabilities"
                       selected={view === "networth"}
                       onClick={onViewChange}
                       drawerOpen={true}
                       expanded={false}
          />
          <NavListItem id="budget"
                       icon={<MonetizationOnIcon/>}
                       title="Budget"
                       primary="Budget"
                       selected={view === "budget"}
                       onClick={onViewChange}
                       drawerOpen={true}
                       expanded={false}
          />

        </List>
      </Drawer>
    )
  }

  if (isLoading) {
    return null
  }

  if (showDialog && currentReview) {
    return (
      <Page title={getPageTitle(view)} backgroundColor="paper">
        <MarginRow>
          <ReviewDialog review={currentReview}
                        onClose={() => navigate(RoutesConfig.calc.pathname)}
                        onSubmit={(review: Review) => navigate(RoutesConfig.calc.pathname)}
                        onDecline={(review: Review) => navigate(RoutesConfig.calc.pathname)}
          />
        </MarginRow>
      </Page>
    )
  }

  return(
    <Page title={getPageTitle(view)} backgroundColor="paper">
      <MarginRow>
        {renderDrawer()}

        <Box component="main" sx={{ flexGrow:1, ml:`${leftMargin}px`, minHeight: "calc(100vh - 206px)"}}>
          {view === 'checklist' && currentReview && currentModel &&
            <ReviewChecklist review={currentReview} model={currentModel} updatedAt={currentUpdatedAt} step={ReviewStep.Checklist}
                             onChange={(review: Review) => setCurrentReview(review)}
                             onChangeView={handleChangeView}
            />
          }

          {view === 'timeline' && currentReview && currentModel &&
            <TimelineReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt}
                            onChange={(item: IModelItem) => setCurrentUpdatedAt(item.updatedAt)}
                            onChangeView={handleChangeView}
            />
          }

          {view === 'networth' && currentReview && currentModel &&
            <NetWorthReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt}
                            onChange={(item: IModelItem) => setCurrentUpdatedAt(item.updatedAt)}
                            onChangeView={handleChangeView}
            />
          }

          {view === 'budget' && currentReview && currentModel &&
            <BudgetReview review={currentReview} model={currentModel} updatedAt={currentUpdatedAt}
                          onChange={(item: IModelItem) => setCurrentUpdatedAt(item.updatedAt)}
                          onChangeView={handleChangeView}
            />
          }
        </Box>
      </MarginRow>
      {showNewReviewDialog &&
        <NewReviewDialog user={client}
                         onChange={(client: User) => setClient(client)}
                         onClose={() => setShowNewReviewDialog(false)}
                         onCreate={(review: Review) => {
                           setShowNewReviewDialog(false)
                           handleCreateReview(review)
                         }}
        />
      }
      {editReview &&
        <EditReviewDialog review={editReview}
                          onClose={() => setEditReview(undefined)}
                          onSave={(review: Review) => {
                             setEditReview(undefined)
                             handleSaveReview(review)
                           }}
                          onDelete={(review: Review) => {
                            setEditReview(undefined)
                            handleDeleteReview(review)
                          }}
          />
      }
    </Page>
  )
})

export default ReviewPage